import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  Form,
  Input,
  MessagePlugin,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  type PrimaryTableCol,
} from 'tdesign-react';
import {
  Plus, Pencil, Trash2, Search, X,
  Mail, Phone, MapPin, Building2, UserCircle,
} from 'lucide-react';
import { businessCardApi, tokenStorage } from '../../api/index';
import { PreviewableImage } from './components/ImagePreview';

const { FormItem } = Form;
const { Option } = Select;

interface BusinessCardFormState {
  id?: number;
  name: string;
  phone: string;
  position: string;
  email: string;
  company: string;
  address: string;
  status: number;
}

const emptyForm: BusinessCardFormState = {
  name: '',
  phone: '',
  position: '',
  email: '',
  company: '',
  address: '',
  status: 1,
};

export default function EcardManage() {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState<number | undefined>(undefined);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<any | null>(null);
  const [formData, setFormData] = useState<BusinessCardFormState>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef<any>(null);

  const fetchData = async (params: { pageNum?: number; pageSize?: number; keyword?: string; status?: number } = {}) => {
    setLoading(true);
    try {
      const res = await businessCardApi.getPageList({
        pageNum: params.pageNum ?? pagination.current,
        pageSize: params.pageSize ?? pagination.pageSize,
        keyword: params.keyword ?? keyword ?? undefined,
        status: params.status ?? status ?? undefined,
      });
      if (res.code === 200) {
        setData(res.data.list || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data.total || 0,
          ...(params.pageNum ? { current: params.pageNum } : null),
          ...(params.pageSize ? { pageSize: params.pageSize } : null),
        }));
      } else {
        MessagePlugin.error(res.message || '加载列表失败');
      }
    } catch {
      MessagePlugin.error('加载列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData({ pageNum: 1, keyword });
  };

  const handleReset = () => {
    setKeyword('');
    setStatus(undefined);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData({ pageNum: 1, keyword: '', status: undefined });
  };

  const handlePageChange = (pageInfo: { current: number; pageSize: number }) => {
    setPagination((prev) => ({ ...prev, current: pageInfo.current, pageSize: pageInfo.pageSize }));
    fetchData({ pageNum: pageInfo.current, pageSize: pageInfo.pageSize });
  };

  const openCreate = () => {
    setEditingRecord(null);
    setFormData(emptyForm);
    setDialogVisible(true);
  };

  const openEdit = async (record: any) => {
    try {
      const res = await businessCardApi.getById(record.id as number);
      if (res.code === 200 && res.data) {
        const row = res.data;
        setEditingRecord(row);
        setFormData({
          id: row.id,
          name: row.name || '',
          phone: row.phone || '',
          position: row.position || '',
          email: row.email || '',
          company: row.company || '',
          address: row.address || '',
          status: row.status ?? 1,
        });
        setDialogVisible(true);
      } else {
        MessagePlugin.error(res.message || '获取详情失败');
      }
    } catch {
      MessagePlugin.error('获取详情失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await businessCardApi.delete(id);
      if (res.code === 200) {
        MessagePlugin.success('删除成功');
        setSelectedRowKeys((prev) => prev.filter((key) => key !== id));
        fetchData();
      } else {
        MessagePlugin.error(res.message || '删除失败');
      }
    } catch {
      MessagePlugin.error('删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const result = await formRef.current?.validate?.();
      if (result !== true) return;

      setSubmitting(true);

      // 获取租户信息
      const tenantInfo = tokenStorage.getTenantInfo();

      const payload: any = {
        name: formData.name,
        phone: formData.phone,
        position: formData.position,
        email: formData.email,
        company: formData.company,
        address: formData.address,
        status: formData.status,
        tenantId: tenantInfo?.id,
        tenantType: tenantInfo?.tenantType,
      };

      const res = editingRecord?.id
        ? await businessCardApi.update(editingRecord.id, payload)
        : await businessCardApi.create(payload);

      if (res.code === 200) {
        MessagePlugin.success(editingRecord ? '更新成功' : '创建成功');
        setDialogVisible(false);
        setEditingRecord(null);
        setFormData(emptyForm);
        fetchData();
      } else {
        MessagePlugin.error(res.message || '保存失败');
      }
    } catch {
      MessagePlugin.error('保存失败');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = useMemo<PrimaryTableCol<any>[]>(() => [
    {
      colKey: 'name',
      title: '姓名',
      ellipsis: true,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {row.avatar ? (
            <PreviewableImage src={row.avatar} alt="avatar" className="w-8 h-8 rounded-full object-cover shrink-0" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
              <UserCircle className="w-5 h-5 text-gray-400" />
            </div>
          )}
          <span className="text-sm font-medium text-gray-800 truncate">{row.name || '-'}</span>
        </div>
      ),
    },
    {
      colKey: 'phone',
      title: '手机号',
      ellipsis: true,
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm text-gray-700">
          <Phone className="w-3 h-3 text-gray-400 shrink-0" />
          <span className="truncate">{row.phone || '-'}</span>
        </div>
      ),
    },
    {
      colKey: 'position',
      title: '职位',
      ellipsis: true,
      cell: ({ row }) => (
        <span className="text-sm text-gray-700 truncate block">{row.position || '-'}</span>
      ),
    },
    {
      colKey: 'email',
      title: '邮箱',
      ellipsis: true,
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm text-gray-700">
          <Mail className="w-3 h-3 text-gray-400 shrink-0" />
          <span className="truncate">{row.email || '-'}</span>
        </div>
      ),
    },
    {
      colKey: 'company',
      title: '公司',
      ellipsis: true,
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm text-gray-700">
          <Building2 className="w-3 h-3 text-gray-400 shrink-0" />
          <span className="truncate">{row.company || '-'}</span>
        </div>
      ),
    },
    {
      colKey: 'address',
      title: '地址',
      ellipsis: true,
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm text-gray-700">
          <MapPin className="w-3 h-3 text-gray-400 shrink-0" />
          <span className="truncate">{row.address || '-'}</span>
        </div>
      ),
    },
    {
      colKey: 'status',
      title: '状态',
      width: 90,
      cell: ({ row }) => (
        <Tag theme={row.status === 1 ? 'success' : 'default'} variant="light">
          {row.status === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      colKey: 'action',
      title: '操作',
      width: 140,
      cell: ({ row }) => (
        <Space>
          <Button theme="primary" variant="text" size="small" icon={<Pencil className="w-4 h-4" />} onClick={() => openEdit(row)}>
            编辑
          </Button>
          <Popconfirm content="确定删除该人员吗？" onConfirm={() => handleDelete(row.id as number)}>
            <Button theme="danger" variant="text" size="small" icon={<Trash2 className="w-4 h-4" />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ], []);

  return (
    <div className="flex-1 w-full min-w-0">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold text-gray-900">电子名片管理</h1>
        <Button theme="primary" icon={<Plus className="w-4 h-4" />} onClick={openCreate}>
          新增名片
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500 whitespace-nowrap">搜索</label>
            <Input
              value={keyword}
              onChange={(val) => setKeyword(val)}
              placeholder="姓名/手机号/公司"
              clearable
              onClear={() => handleReset()}
              style={{ width: 220 }}
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500 whitespace-nowrap">状态</label>
            <Select
              value={status}
              onChange={(val) => setStatus(val as number | undefined)}
              clearable
              placeholder="全部"
              style={{ width: 120 }}
            >
              <Option value={1}>启用</Option>
              <Option value={0}>禁用</Option>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button theme="primary" icon={<Search className="w-4 h-4" />} onClick={handleSearch}>搜索</Button>
            <Button icon={<X className="w-4 h-4" />} onClick={handleReset}>重置</Button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          data={data}
          selectedRowKeys={selectedRowKeys}
          onSelectChange={(keys: any) => setSelectedRowKeys(keys)}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showJumper: true,
            showPageSize: true,
            pageSizeOptions: [10, 20, 50],
            onChange: handlePageChange,
          }}
          bordered={false}
          hover
          size="small"
        />
      </div>

      <Dialog
        visible={dialogVisible}
        header={editingRecord ? '编辑名片' : '新增名片'}
        confirmBtn="保存"
        onClose={() => {
          setDialogVisible(false);
          setEditingRecord(null);
          setFormData(emptyForm);
        }}
        onConfirm={handleSubmit}
        confirmLoading={submitting}
        width={720}
      >
        <Form ref={formRef} labelWidth={110} className="max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormItem label="姓名" name="name" rules={[{ required: true, message: '请输入姓名' }]}>
              <Input value={formData.name} onChange={(val) => setFormData((prev) => ({ ...prev, name: val }))} placeholder="请输入姓名" />
            </FormItem>

            <FormItem label="手机号" name="phone" rules={[
              { required: true, message: '请输入手机号' },
              { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号格式' }
            ]}>
              <Input value={formData.phone} onChange={(val) => setFormData((prev) => ({ ...prev, phone: val }))} placeholder="请输入手机号" />
            </FormItem>

            <FormItem label="职位" name="position" rules={[{ required: true, message: '请输入职位' }]}>
              <Input value={formData.position} onChange={(val) => setFormData((prev) => ({ ...prev, position: val }))} placeholder="请输入职位" />
            </FormItem>

            <FormItem label="邮箱" name="email" rules={[
              { required: true, message: '请输入邮箱' },
              { pattern: /^[\w.-]+@[\w-]+\.[\w.-]+$/, message: '请输入正确的邮箱格式' }
            ]}>
              <Input value={formData.email} onChange={(val) => setFormData((prev) => ({ ...prev, email: val }))} placeholder="请输入邮箱" />
            </FormItem>

            <FormItem label="公司" name="company" rules={[{ required: true, message: '请输入公司' }]}>
              <Input value={formData.company} onChange={(val) => setFormData((prev) => ({ ...prev, company: val }))} placeholder="请输入公司" />
            </FormItem>

            <FormItem label="地址" name="address" rules={[{ required: true, message: '请输入地址' }]}>
              <Input value={formData.address} onChange={(val) => setFormData((prev) => ({ ...prev, address: val }))} placeholder="请输入地址" />
            </FormItem>

            <FormItem label="状态" name="status">
              <Switch
                value={formData.status === 1}
                onChange={(val) => setFormData((prev) => ({ ...prev, status: val ? 1 : 0 }))}
              />
            </FormItem>
          </div>
        </Form>
      </Dialog>
    </div>
  );
}
